import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function AdminDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    vendor: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        ...filters
      });

      const response = await axios.get(
        `${API_URL}/api/admin/submissions?${params}`,
        { withCredentials: true }
      );

      setSubmissions(response.data.submissions);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        total: response.data.total
      }));
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/stats`,
        { withCredentials: true }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, [fetchSubmissions, fetchStats]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/logout`, {}, { withCredentials: true });
      navigate('/admin');
    } catch (err) {
      console.error('Logout failed');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(
        `${API_URL}/api/admin/export?${params}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submissions-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/submissions/${id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      fetchSubmissions();
      fetchStats();
    } catch (err) {
      console.error('Status update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/submissions/${id}`,
        { withCredentials: true }
      );
      fetchSubmissions();
      fetchStats();
    } catch (err) {
      console.error('Delete failed');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">SNA Renewals</div>
        <nav>
          <ul className="admin-nav">
            <li>
              <Link to="/admin/dashboard" className="active">📊 Dashboard</Link>
            </li>
            <li>
              <button onClick={() => setShowPasswordModal(true)}>🔒 Change Password</button>
            </li>
            <li>
              <Link to="/">📝 View Form</Link>
            </li>
            <li>
              <button onClick={handleLogout}>🚪 Logout</button>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="admin-main">
        <div className="admin-header">
          <h1>Dashboard</h1>
          <button className="btn btn-primary" onClick={handleExport}>📥 Export CSV</button>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Submissions</h3>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card pending">
            <h3>Pending</h3>
            <div className="stat-value">{stats.pending}</div>
          </div>
          <div className="stat-card completed">
            <h3>Completed</h3>
            <div className="stat-value">{stats.completed}</div>
          </div>
          <div className="stat-card today">
            <h3>Today</h3>
            <div className="stat-value">{stats.today}</div>
          </div>
        </div>

        <div className="filters-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search by site name, HTL code, contact..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={filters.vendor}
            onChange={(e) => handleFilterChange('vendor', e.target.value)}
          >
            <option value="all">All Vendors</option>
            <option value="meraki">Meraki</option>
            <option value="aruba">Aruba</option>
            <option value="cisco">Cisco</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="data-table">
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
          ) : submissions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              No submissions found
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Site Name</th>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>Exp. Date</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id}>
                    <td>#{sub.id}</td>
                    <td>
                      <strong>{sub.site_name}</strong>
                      {sub.htl_code && <small style={{ display: 'block', color: '#6b7280' }}>{sub.htl_code}</small>}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{sub.vendor}</td>
                    <td>{sub.renewal_type}</td>
                    <td>{new Date(sub.expiration_date).toLocaleDateString()}</td>
                    <td>
                      <select
                        className={`badge badge-${sub.status}`}
                        value={sub.status}
                        onChange={(e) => handleStatusChange(sub.id, e.target.value)}
                        style={{ border: 'none', cursor: 'pointer', padding: '0.25rem 0.5rem', borderRadius: '9999px' }}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>{formatDate(sub.created_at)}</td>
                    <td className="actions">
                      <Link to={`/admin/submission/${sub.id}`} className="btn btn-secondary btn-sm">View</Link>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(sub.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </button>
            {[...Array(pagination.totalPages)].map((_, i) => (
              <button
                key={i + 1}
                className={pagination.page === i + 1 ? 'active' : ''}
                onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
              >
                {i + 1}
              </button>
            )).slice(Math.max(0, pagination.page - 3), Math.min(pagination.totalPages, pagination.page + 2))}
            <button
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
          Showing {submissions.length} of {pagination.total} submissions
        </p>
      </main>

      {showPasswordModal && <PasswordModal onClose={() => setShowPasswordModal(false)} />}
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API_URL}/api/admin/change-password`,
        { currentPassword, newPassword },
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Change Password</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {success ? (
            <div className="alert alert-success">Password changed successfully!</div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-error"><span>{error}</span></div>}
              <div className="form-group">
                <label>Current Password</label>
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
